import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@clerk/react";
import { Loader2, CheckCircle, XCircle, LogIn } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";
import { PageMeta } from "@/components/PageMeta";

export function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  const { isSignedIn: isAuthenticated } = useAuth();
  const login = () => navigate("/sign-in");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error" | "already_active">("idle");
  const [message, setMessage] = useState("");
  const [workspaceName, setWorkspaceName] = useState("");

  useEffect(() => {
    if (!token) return;

    if (!isAuthenticated) {
      setStatus("idle");
      return;
    }

    setStatus("loading");
    apiFetch(`/api/team/join/${token}`, { credentials: "include" })
      .then(async (r) => {
        const data = await r.json();
        if (r.ok) {
          if (data.message === "already_active") {
            setStatus("already_active");
            setWorkspaceName(data.member?.name ?? "the workspace");
          } else {
            setStatus("success");
            setWorkspaceName(data.member?.name ?? "the workspace");
            setTimeout(() => navigate("/"), 2500);
          }
        } else {
          setStatus("error");
          setMessage(data.error ?? "Failed to accept invite");
          if (data.redirect) {
            setTimeout(() => login(), 1500);
          }
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Network error. Please try again.");
      });
  }, [token, isAuthenticated]);

  return (
    <>
      <PageMeta
        title="Team Invitation — LaunchTactix"
        description="Accept your LaunchTactix team invitation to collaborate on your AI growth platform workspace."
        canonical="https://launchtactix.com/invite"
      />
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-card border rounded-2xl p-8 shadow-lg text-center space-y-4">
        <h1 className="text-2xl font-bold">Team Invitation</h1>

        {status === "idle" && !isAuthenticated && (
          <>
            <p className="text-muted-foreground">
              You must be signed in to accept this invitation. Please log in with the email address the invite was sent to.
            </p>
            <button
              onClick={() => login()}
              className="inline-flex items-center gap-2 mt-4 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              <LogIn className="h-4 w-4" />
              Sign In to Accept
            </button>
          </>
        )}

        {status === "loading" && (
          <>
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
            <p className="text-muted-foreground">Accepting your invitation…</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <p className="text-lg font-semibold">Welcome to the team!</p>
            <p className="text-muted-foreground">
              Your invitation has been accepted. Redirecting you to the dashboard…
            </p>
          </>
        )}

        {status === "already_active" && (
          <>
            <CheckCircle className="h-12 w-12 text-blue-500 mx-auto" />
            <p className="text-lg font-semibold">Already a member</p>
            <p className="text-muted-foreground">
              You are already an active member of this workspace.
            </p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 mt-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-lg font-semibold">Invitation Error</p>
            <p className="text-muted-foreground">{message}</p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 mt-2 px-6 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-colors"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </div>
    </div>
    </>
  );
}
